/**
 * Skeleton — shimmering placeholders shown while data is loading.
 *
 * Improves perceived TTV (time-to-value) on slow connections — without
 * skeletons users see a blank panel during the initial events fetch and
 * may bounce thinking the site is broken. Especially important on the
 * Mobile sidebar where the bottom-sheet handle implies "something below".
 *
 * The shimmer is CSS-keyframe driven and respects `prefers-reduced-motion`
 * (handled in App.css with the global `* { animation: none !important; }`
 * rule for that media query).
 */
import { CSSProperties } from 'react';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  radius?: number;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({
  width = '100%',
  height = 16,
  radius = 6,
  className = '',
  style = {},
}: SkeletonProps) {
  return (
    <div
      className={`skeleton ${className}`}
      style={{
        width,
        height,
        borderRadius: radius,
        ...style,
      }}
      aria-hidden="true"
    />
  );
}

/** A single fire-card placeholder matching CompactFireCard's rough layout. */
function SkeletonFireCard() {
  return (
    <div className="skeleton-fire-card" aria-hidden="true">
      <div className="skeleton-fire-card-bar" />
      <div className="skeleton-fire-card-body">
        <Skeleton width="70%" height={14} />
        <Skeleton width="50%" height={11} style={{ marginTop: 6 }} />
      </div>
    </div>
  );
}

/** A vertical stack of fire-card skeletons. Default 5 cards. */
export function SkeletonFireCardList({ count = 5 }: { count?: number }) {
  return (
    <div className="skeleton-fire-card-list" role="status" aria-label="Loading fires">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonFireCard key={i} />
      ))}
    </div>
  );
}
